import * as React from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";
import * as ImagePicker from "expo-image-picker";
import { useRouter } from "expo-router";
import { Feather } from "@expo/vector-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import * as z from "zod";

import { displayNameToSlug, FieldSync } from "~/hooks/useFieldSync";
import { useGetR2AssetURL } from "~/hooks/useGetR2AssetURL";
import { useUploadObject } from "~/hooks/useUploadObject";
import { isWebDesktop } from "~/lib/constants";
import { generateObjectId } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { Button } from "~/ui/Button";
import { TextInput } from "~/ui/TextInput";

export function SpaceForm({
  spaceId,
  inBottomSheet,
  onSuccess,
}: {
  spaceId?: string;
  onSuccess?: () => void;
  inBottomSheet?: boolean;
}) {
  const newSpaceId = React.useRef(generateObjectId());

  const { uploadObject } = useUploadObject();

  const { store } = useAccountStore();
  const space = spaceId ? store.spaces.get(spaceId) : null;

  const validationSchema = z.object({
    displayName: z.string().min(1),
    slug: z
      .string()
      .min(1)
      .refine(
        (slug) => {
          const spaceWithSameSlug = store.spaceBySlug(slug);
          return !spaceWithSameSlug || spaceWithSameSlug.id === space?.id;
        },
        { message: "Slug is already in use." },
      ),
    r2Key: z.string().optional(),
  });

  const form = useForm<z.infer<typeof validationSchema>>({
    resolver: zodResolver(validationSchema),
    defaultValues: space
      ? {
          displayName: space.displayName ?? "",
          slug: space.slug,
          r2Key: space.r2Key ?? undefined,
        }
      : {
          displayName: "",
          slug: "",
          r2Key: undefined,
        },
  });
  const { control, handleSubmit, setFocus, setValue } = form;

  const r2Key = useWatch({ control, name: "r2Key" });

  const { push, back } = useRouter();

  async function handleImagePick() {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
      allowsMultipleSelection: false,
    });

    if (!result.canceled && result.assets[0]) {
      const uri = result.assets[0].uri;

      const fileSize = await getFileSize(uri);

      if (fileSize > 5 * 1024 * 1024) {
        alert("Image size must be less than 5MB");
        return;
      }

      try {
        const data = await uploadObject({
          file: {
            uri,
            name: result.assets[0].fileName ?? "workspace-image.jpg",
            type: result.assets[0].type ?? "image",
            mimeType: result.assets[0].mimeType ?? "image/jpeg",
          },
          objectType: "workspace",
          objectId: spaceId || newSpaceId.current,
        });
        if (data.data?.key) {
          setValue("r2Key", data.data.key);
        }
      } catch (error) {
        console.error("Failed to upload image:", error);
      }
    }
  }

  async function getFileSize(uri: string) {
    const response = await fetch(uri);
    const blob = await response.blob();
    return blob.size;
  }

  async function onSubmit(data: z.infer<typeof validationSchema>) {
    try {
      if (space) {
        store.updateSpace({
          spaceId: space.id,
          set: data,
        });
      } else {
        store.createSpace({
          set: {
            ...data,
            id: newSpaceId.current,
          },
        });
      }

      // all these conditional behaviors should be in AccountActions.
      // but because native is route, idk (yet) how to do this
      if (!isWebDesktop()) {
        // TODO: revisit this, should be `back()`?
        push("../");
      }

      setTimeout(() => {
        push({
          pathname: "/(app)/(authenticated)/[account]/(space)/[space]",
          params: {
            account: store.slug,
            space: data.slug,
          },
        });
      }, 0);

      onSuccess?.();
    } catch (error) {
      console.error("Failed to save workspace:", error);
      alert("Failed to save workspace. Please try again.");
    }
  }

  const { getR2AssetURL } = useGetR2AssetURL();
  return (
    <FormProvider {...form}>
      <View className="gap-y-5 p-4">
        <TouchableOpacity
          onPress={handleImagePick}
          className="items-center justify-center"
        >
          {r2Key ? (
            <View className="relative h-24 w-24 overflow-hidden rounded-full">
              <Image
                source={{ uri: getR2AssetURL(r2Key) }}
                className="h-full w-full"
              />
              <View className="absolute bottom-0 left-0 right-0 bg-black/50 p-1">
                <Text className="text-center text-xs text-white">Change</Text>
              </View>
            </View>
          ) : (
            <View className="border-divider h-24 w-24 items-center justify-center rounded-full border-2 border-dashed">
              <Feather name="image" size={24} className="text-icon" />
              <Text className="text-text-secondary text-sm">Add Image</Text>
            </View>
          )}
        </TouchableOpacity>

        <Controller
          control={control}
          name="displayName"
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              inBottomSheet={inBottomSheet}
              label="Title"
              onChangeText={field.onChange}
              value={field.value ?? ""}
              autoFocus
              errorMessage={error?.message}
              enterKeyHint="next"
              onSubmitEditing={() => {
                setFocus("slug");
              }}
            />
          )}
        />

        <FieldSync
          source="displayName"
          destination="slug"
          transformFn={displayNameToSlug}
        />

        <Controller
          control={control}
          name="slug"
          render={({ field, fieldState: { error } }) => (
            <TextInput
              {...field}
              inBottomSheet={inBottomSheet}
              label="Slug"
              onChangeText={field.onChange}
              value={field.value}
              errorMessage={error?.message}
              autoCapitalize="none"
              enterKeyHint="done"
              onSubmitEditing={handleSubmit(onSubmit)}
            />
          )}
        />

        {/* <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <TextInput
              inBottomSheet
              multiline
              numberOfLines={4}
              label="Description"
              onChangeText={field.onChange}
              value={field.value}
            />
          )}
        /> */}

        {/* <View className="items-center flex-row gap-2">
          <Switch />
          <Text className="text-button-label-primary">Public</Text>
        </View> */}

        <View />
        <Button onPress={handleSubmit(onSubmit)}>
          {spaceId ? "Update workspace" : "Create workspace"}
        </Button>
        <View />
      </View>
    </FormProvider>
  );
}
